import { useEffect, useMemo, useRef, useState } from "react";
import {
  Minus,
  Maximize2,
  Minimize2,
  X,
  Trash2,
  Send,
  ChevronDown,
  Paperclip,
  FileText,
} from "lucide-react";
import { useAppDispatch } from "@/stores/hook";
import {
  closeCompose,
  minimizeCompose,
  toggleComposeFullscreen,
} from "../stores/gmailSlice";
import { useEmailTemplates, useRenderTemplate } from "../api/useEmailTemplates";
import { useSendGmailMessage } from "../api/useGmailMutations";
import { GMAIL_TOKENS } from "../constants/designTokens";
import type { ComposeState } from "../stores/gmailSlice";
import type { SendAttachmentPayload } from "../types/gmail.types";

interface Props {
  state: ComposeState;
  onClose: () => void;
  fromAddress: string;
}

interface DragOffset {
  x: number;
  y: number;
}

const DEFAULT_RIGHT = 24;
const DEFAULT_BOTTOM = 24;
const WIDTH = 540;
const HEIGHT = 540;
const MAX_ATTACHMENTS_BYTES = 25 * 1024 * 1024;

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => {
      const result = String(reader.result || "");
      resolve(result.includes(",") ? result.split(",")[1] : result);
    };
    reader.readAsDataURL(file);
  });
}

/**
 * Bottom-right Gmail-style compose. Behaviour replicated from web.gmail.com:
 *  - Drag from header
 *  - Click "−" to dock as a strip in the bottom-right
 *  - Click "⛶" to toggle full-screen overlay
 *  - Click "✕" to discard (with confirm if there's unsaved content)
 *
 * Templating: when a `templateId` prop is provided (Quick Send flow), we
 * pre-render against the lead context server-side so the user sees the final
 * email; otherwise the user can pick a template from the dropdown which fires
 * the same render endpoint and replaces the body.
 */
export default function ComposeModal({ state, onClose, fromAddress }: Props) {
  const dispatch = useAppDispatch();

  const [pos, setPos] = useState<DragOffset | null>(null);
  const dragRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    pos: DragOffset;
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [to, setTo] = useState((state.prefill?.to ?? []).join(", "));
  const [cc, setCc] = useState((state.prefill?.cc ?? []).join(", "));
  const [bcc, setBcc] = useState((state.prefill?.bcc ?? []).join(", "));
  const [showCcBcc, setShowCcBcc] = useState(
    (state.prefill?.cc?.length ?? 0) + (state.prefill?.bcc?.length ?? 0) > 0,
  );
  const [subject, setSubject] = useState(state.prefill?.subject ?? "");
  const [htmlBody, setHtmlBody] = useState(state.prefill?.htmlBody ?? "");
  const [templateId, setTemplateId] = useState<number | null>(
    state.templateId ?? null,
  );
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  // What the last template render produced. The server RE-RENDERS any send
  // that carries a `templateId` (against `context`), so the template rides the
  // send only while the subject and body are still exactly that render;
  // once the user edits them, the edited text goes out as written.
  const [rendered, setRendered] = useState<{ subject: string; html: string } | null>(null);
  const [attachments, setAttachments] = useState<SendAttachmentPayload[]>([]);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);

  const { templates } = useEmailTemplates();
  const renderMut = useRenderTemplate();
  const sendMut = useSendGmailMessage();
  const totalAttachmentBytes = attachments.reduce(
    (sum, file) => sum + file.sizeBytes,
    0,
  );

  // If launched with a templateId we render once on mount.
  useEffect(() => {
    if (state.templateId && !subject && !htmlBody) {
      renderMut.mutate(
        {
          id: state.templateId,
          context: {
            ...(state.leadId ? { leadId: state.leadId } : {}),
          },
        },
        {
          onSuccess: (data) => {
            setSubject(data.subject);
            setHtmlBody(data.html);
            setRendered({ subject: data.subject, html: data.html });
          },
        },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.templateId]);

  const clampPosition = (next: DragOffset): DragOffset => {
    const modalWidth = state.minimized ? 340 : WIDTH;
    const modalHeight = state.minimized ? 44 : HEIGHT;
    const minRight = 8;
    const maxRight = Math.max(minRight, window.innerWidth - modalWidth - 8);
    const minBottom = state.minimized ? 0 : 8;
    const maxBottom = Math.max(minBottom, window.innerHeight - modalHeight - 8);

    const right = Math.min(
      Math.max(DEFAULT_RIGHT + next.x, minRight),
      maxRight,
    );
    const bottom = Math.min(
      Math.max((state.minimized ? 0 : DEFAULT_BOTTOM) + next.y, minBottom),
      maxBottom,
    );

    return {
      x: right - DEFAULT_RIGHT,
      y: bottom - (state.minimized ? 0 : DEFAULT_BOTTOM),
    };
  };

  const handleDragStart = (e: React.PointerEvent<HTMLElement>) => {
    if (state.fullscreen || e.button !== 0) return;
    if ((e.target as HTMLElement).closest("button")) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      pos: pos ?? { x: 0, y: 0 },
    };
  };

  const handleDragMove = (e: React.PointerEvent<HTMLElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    const next = {
      x: drag.pos.x - (e.clientX - drag.startX),
      y: drag.pos.y - (e.clientY - drag.startY),
    };
    setPos(clampPosition(next));
  };

  const handleDragEnd = (e: React.PointerEvent<HTMLElement>) => {
    if (dragRef.current?.pointerId === e.pointerId) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    dragRef.current = null;
  };

  const handleSend = () => {
    const toList = to
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    if (toList.length === 0) return;

    // Until 2026-10-21 the send carried `templateId` with no `context`, so the
    // server re-rendered the template with every lead field blank and threw
    // away any edit made after picking it.
    const templateUntouched =
      templateId != null &&
      rendered != null &&
      subject === rendered.subject &&
      htmlBody === rendered.html;

    sendMut.mutate(
      {
        to: toList,
        cc: cc.split(",").map((s) => s.trim()).filter(Boolean),
        bcc: bcc.split(",").map((s) => s.trim()).filter(Boolean),
        subject,
        htmlBody,
        ...(templateUntouched
          ? {
              templateId: templateId ?? undefined,
              context: state.leadId ? { leadId: state.leadId } : {},
            }
          : {}),
        leadId: state.leadId,
        dealId: state.dealId,
        attachments,
      },
      { onSuccess: () => dispatch(closeCompose()) },
    );
  };

  const handleSelectAttachments = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = "";
    if (files.length === 0) return;

    const incomingBytes = files.reduce((sum, file) => sum + file.size, 0);
    if (totalAttachmentBytes + incomingBytes > MAX_ATTACHMENTS_BYTES) {
      setAttachmentError("Attachments can be up to 25 MB per email.");
      return;
    }

    try {
      const next = await Promise.all(
        files.map(async (file) => ({
          filename: file.name,
          mimeType: file.type || "application/octet-stream",
          sizeBytes: file.size,
          contentBase64: await readFileAsBase64(file),
        })),
      );
      setAttachments((prev) => [...prev, ...next]);
      setAttachmentError(null);
    } catch {
      setAttachmentError("Could not read one of the selected files.");
    }
  };

  const removeAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handlePickTemplate = (id: number) => {
    setTemplateId(id);
    setShowTemplatePicker(false);
    renderMut.mutate(
      { id, context: state.leadId ? { leadId: state.leadId } : {} },
      {
        onSuccess: (data) => {
          setSubject(data.subject);
          setHtmlBody(data.html);
          setRendered({ subject: data.subject, html: data.html });
        },
      },
    );
  };

  const containerStyle = useMemo<React.CSSProperties>(() => {
    if (state.fullscreen) {
      return {
        position: "fixed",
        inset: "5%",
        background: GMAIL_TOKENS.card,
        zIndex: 60,
      };
    }
    if (state.minimized) {
      return {
        position: "fixed",
        right: DEFAULT_RIGHT,
        bottom: 0,
        width: 340,
        height: 44,
        background: GMAIL_TOKENS.card,
        zIndex: 60,
      };
    }
    return {
      position: "fixed",
      right: DEFAULT_RIGHT + (pos?.x ?? 0),
      bottom: DEFAULT_BOTTOM + (pos?.y ?? 0),
      width: WIDTH,
      height: HEIGHT,
      background: GMAIL_TOKENS.card,
      zIndex: 60,
    };
  }, [state.fullscreen, state.minimized, pos]);

  return (
    <div
      style={{
        ...containerStyle,
        boxShadow:
          "0 8px 10px rgba(60,64,67,0.08), 0 16px 32px rgba(60,64,67,0.18)",
        borderRadius: state.minimized ? "8px 8px 0 0" : "8px",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        border: `1px solid ${GMAIL_TOKENS.hairline}`,
      }}
    >
      <header
        onPointerDown={handleDragStart}
        onPointerMove={handleDragMove}
        onPointerUp={handleDragEnd}
        onPointerCancel={handleDragEnd}
        className="flex h-10 select-none items-center px-3 text-[13px] font-medium"
        style={{
          background: "#3c4043",
          borderTop: `3px solid ${GMAIL_TOKENS.gmailRed}`,
          color: "#fff",
          cursor: state.fullscreen ? "default" : "move",
          flex: "0 0 auto",
          touchAction: "none",
        }}
      >
        <span className="flex-1 truncate">
          {subject || "New message"}
        </span>
        <button
          onClick={() => dispatch(minimizeCompose(!state.minimized))}
          className="ml-1 flex h-6 w-6 items-center justify-center rounded hover:bg-white/10"
          aria-label="Minimize"
        >
          <Minus size={14} />
        </button>
        <button
          onClick={() => dispatch(toggleComposeFullscreen())}
          className="ml-1 flex h-6 w-6 items-center justify-center rounded hover:bg-white/10"
          aria-label={state.fullscreen ? "Exit fullscreen" : "Fullscreen"}
        >
          {state.fullscreen ? <Minimize2 size={13} /> : <Maximize2 size={13} />}
        </button>
        <button
          onClick={onClose}
          className="ml-1 flex h-6 w-6 items-center justify-center rounded hover:bg-white/10"
          aria-label="Close"
        >
          <X size={14} />
        </button>
      </header>

      {!state.minimized && (
        <div className="flex flex-1 flex-col overflow-hidden">
          <div className="px-4 py-2" style={{ borderBottom: `1px solid ${GMAIL_TOKENS.hairline}` }}>
            <RowField
              label="From"
              readOnly
              value={fromAddress}
            />
            <RowField
              label="To"
              value={to}
              onChange={setTo}
              right={
                !showCcBcc && (
                  <button
                    onClick={() => setShowCcBcc(true)}
                    className="text-[12px]"
                    style={{ color: GMAIL_TOKENS.read }}
                  >
                    Cc Bcc
                  </button>
                )
              }
            />
            {showCcBcc && (
              <>
                <RowField label="Cc" value={cc} onChange={setCc} />
                <RowField label="Bcc" value={bcc} onChange={setBcc} />
              </>
            )}
            <RowField
              label="Subject"
              value={subject}
              onChange={setSubject}
              isSubject
            />
          </div>

          <div className="relative flex flex-1 flex-col overflow-hidden">
            {/* Body editor — we keep it as a contenteditable div so the user
                gets the typical free-form Gmail body experience without
                pulling in a heavy WYSIWYG dependency for V1. The Template
                Builder (Phase 4b) is where the structured editor lives. */}
            <div
              contentEditable
              suppressContentEditableWarning
              dangerouslySetInnerHTML={{ __html: htmlBody }}
              onInput={(e) =>
                setHtmlBody((e.target as HTMLElement).innerHTML)
              }
              className="flex-1 overflow-y-auto px-4 py-3 text-[14px] focus:outline-none"
              style={{ color: GMAIL_TOKENS.unread, fontFamily: "inherit" }}
            />

            {showTemplatePicker && (
              <div
                className="absolute bottom-14 left-3 z-10 max-h-72 w-72 overflow-y-auto rounded-lg border bg-white shadow-lg"
                style={{ borderColor: GMAIL_TOKENS.hairline }}
              >
                <div
                  className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wide"
                  style={{ color: GMAIL_TOKENS.read, borderBottom: `1px solid ${GMAIL_TOKENS.hairline}` }}
                >
                  Choose a template
                </div>
                {templates.length === 0 ? (
                  <div className="px-3 py-3 text-[12px]" style={{ color: GMAIL_TOKENS.read }}>
                    No templates yet — create one from the Templates tab.
                  </div>
                ) : (
                  templates.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => handlePickTemplate(t.id)}
                      className="block w-full px-3 py-2 text-left text-[13px] hover:bg-[#f6f8fc]"
                    >
                      <div style={{ color: GMAIL_TOKENS.unread, fontWeight: 500 }}>
                        {t.name}
                      </div>
                      <div className="truncate text-[11px]" style={{ color: GMAIL_TOKENS.read }}>
                        {t.subject}
                      </div>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          <footer
            className="flex min-h-12 flex-col gap-2 px-3 py-2"
            style={{ borderTop: `1px solid ${GMAIL_TOKENS.hairline}` }}
          >
            {attachments.length > 0 || attachmentError ? (
              <div className="flex flex-wrap items-center gap-2">
                {attachments.map((file, index) => (
                  <div
                    key={`${file.filename}-${index}`}
                    className="flex max-w-[220px] items-center gap-2 rounded-md border px-2 py-1 text-[12px]"
                    style={{
                      borderColor: GMAIL_TOKENS.hairline,
                      background: GMAIL_TOKENS.bg,
                      color: GMAIL_TOKENS.unread,
                    }}
                    title={`${file.filename} (${formatBytes(file.sizeBytes)})`}
                  >
                    <FileText size={14} style={{ color: GMAIL_TOKENS.read }} />
                    <span className="truncate">{file.filename}</span>
                    <span
                      className="shrink-0 text-[11px]"
                      style={{ color: GMAIL_TOKENS.read }}
                    >
                      {formatBytes(file.sizeBytes)}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeAttachment(index)}
                      className="flex h-5 w-5 shrink-0 items-center justify-center rounded hover:bg-black/10"
                      aria-label={`Remove ${file.filename}`}
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
                {attachmentError ? (
                  <span
                    className="text-[12px]"
                    style={{ color: GMAIL_TOKENS.badgeRed }}
                  >
                    {attachmentError}
                  </span>
                ) : null}
              </div>
            ) : null}

            <div className="flex w-full items-center">
              <button
                onClick={handleSend}
                disabled={sendMut.isPending}
                className="flex h-9 items-center gap-2 rounded-full px-5 text-[14px] font-medium disabled:opacity-50"
                style={{
                  background: GMAIL_TOKENS.actionBg,
                  color: GMAIL_TOKENS.actionFg,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = GMAIL_TOKENS.actionBgHover;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = GMAIL_TOKENS.actionBg;
                }}
              >
                <Send size={14} />
                {sendMut.isPending ? "Sending…" : "Send"}
              </button>

              <button
                onClick={() => setShowTemplatePicker((v) => !v)}
                className="ml-2 flex h-8 items-center gap-1 rounded px-2 text-[12px] font-medium hover:bg-black/5"
                style={{ color: GMAIL_TOKENS.read }}
              >
                Template <ChevronDown size={12} />
              </button>

              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={handleSelectAttachments}
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="ml-1 flex h-8 w-8 items-center justify-center rounded hover:bg-black/5"
                aria-label="Attach files"
                title={`Attach files (${formatBytes(totalAttachmentBytes)} / ${formatBytes(MAX_ATTACHMENTS_BYTES)})`}
              >
                <Paperclip size={16} style={{ color: GMAIL_TOKENS.read }} />
              </button>

              <button
                onClick={onClose}
                className="ml-auto flex h-8 w-8 items-center justify-center rounded hover:bg-black/5"
                aria-label="Discard"
              >
                <Trash2 size={16} style={{ color: GMAIL_TOKENS.read }} />
              </button>
            </div>
          </footer>
        </div>
      )}
    </div>
  );
}

function RowField({
  label,
  value,
  onChange,
  readOnly,
  isSubject,
  right,
}: {
  label: string;
  value: string;
  onChange?: (v: string) => void;
  readOnly?: boolean;
  isSubject?: boolean;
  right?: React.ReactNode;
}) {
  return (
    <div
      className="flex items-center gap-2 py-1.5"
      style={{ borderBottom: `1px solid ${GMAIL_TOKENS.hairline}` }}
    >
      <span
        className="w-12 shrink-0 text-[12px]"
        style={{ color: GMAIL_TOKENS.read }}
      >
        {label}
      </span>
      <input
        readOnly={readOnly}
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        className="flex-1 bg-transparent text-[13px] focus:outline-none"
        style={{
          color: GMAIL_TOKENS.unread,
          fontWeight: isSubject ? 500 : 400,
        }}
        placeholder={isSubject ? "Subject" : ""}
      />
      {right}
    </div>
  );
}
